import { notifyError } from "common/utils/app-monitoring/notify";
import { track } from "jiffy-analytics";
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import { megabitsPerSecond } from "common/utils/helpers/upload-rate";
import { trackRudderstackImageUploadCompleted } from "common/utils/rudderstack/tracker";
import { BUSINESS_LINE } from "../app-registry/contants";

const $getDtfProduct = () => document.querySelector(".js-dtf-product");

const getCatalogNumber = () => {
  const dataset = $getDtfProduct()?.dataset;
  let catalogNumber = dataset?.catalogNumber;

  if (!catalogNumber && dataset?.appConfig) {
    try {
      catalogNumber = JSON.parse(dataset.appConfig)?.product?.catalog_number;
    } catch (error) {
      notifyError("[JS] Failed to parse appConfig JSON for catalogNumber", {
        params: { error: error.message }
      });
    }
  }

  return catalogNumber;
};

export const submitDtfEvent = (event, rawPayload = {}) => {
  const $getOrderNumberContainer = () => document.querySelector(".js-order-number-container");

  const getOrderNumber = () => $getOrderNumberContainer()?.dataset?.orderNumber;

  let payload = {
    order_number: getOrderNumber(),
    ...rawPayload,
    business_line:
      rawPayload && rawPayload.business_line ? rawPayload.business_line : BUSINESS_LINE.DTF
  };

  const catalogNumber = getCatalogNumber();
  if (!payload.catalog_number && catalogNumber) {
    payload = { ...payload, catalog_number: catalogNumber };
  }

  const currentStoreCode = document.documentElement.lang === "en-CA" ? "ca" : "us";
  if (currentStoreCode) {
    payload = { current_store_code: currentStoreCode, ...payload };
  }

  // non-PDP or a previous dynamicDataLoaded already set the order number
  if (getOrderNumber()) {
    track(event, payload);
    return;
  }

  const $scriptElement = document.querySelector('script[src^="/api/pdp_dynamic_data"]');
  if ($scriptElement) {
    waitForWindowVariable(
      "dynamicDataLoaded",
      () => {
        track(event, payload);
      },
      () => {
        notifyError("dynamicDataLoaded failed");
        track(event, payload);
      }
    );
  }
};

// DTF AI Redraw Events
export const submitDtfAiRedrawNoteSubmitted = (artworkId, note) => {
  submitDtfEvent("dtf_ai_redraw_note_submitted", {
    artwork_id: artworkId,
    note,
    page: window.location.pathname
  });
};

export const submitDtfAiRedrawQuestionsAnswered = (artworkId, questions) => {
  submitDtfEvent("dtf_ai_redraw_questions_answered", {
    artwork_id: artworkId,
    questions,
    page: window.location.pathname
  });
};

export const submitDtfAiRedrawInterpretationComplete = (artworkId, step) => {
  submitDtfEvent("dtf_ai_redraw_interpretation_complete", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname
  });
};

export const submitDtfAiRedrawCancelled = (artworkId, step) => {
  submitDtfEvent("dtf_ai_redraw_cancelled", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname
  });
};

export const submitDtfAiRedrawInterpretationFailed = (artworkId, step) => {
  submitDtfEvent("dtf_ai_redraw_interpretation_failed", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname
  });
};

/* eslint-disable max-lines-per-function, camelcase */
export const submitCompletedEvent = rawPayload => {
  const {
    file,
    artwork,
    catalogNumber,
    dpi,
    preCutTransfers,
    artworksCount,
    response,
    multiArtwork,
    pdpType,
    uploadedOn,
    uploadStartTime,
    validationErrors,
    businessLine,
    orderType,
    uploadUuid,
    success: explicitSuccess
  } = rawPayload;

  if (!uploadStartTime) {
    throw new Error("uploadStartTime is required");
  }
  const uploadedFile = file || artwork?.file;
  const upload_speed_megabits_per_second = megabitsPerSecond(
    uploadedFile.size,
    uploadStartTime,
    performance.now()
  );

  const artwork_id = artwork?.id || response?.id;
  const metadata = artwork?.metadata || {};
  const page = uploadedOn || metadata?.uploaded_on;
  const project_id = artwork?.project_id || response?.project_id;
  const image_url = artwork?.url || response?.url;
  const worker_enhancement = artwork?.data?.worker_enhancement ?? false;

  let validation_errors = validationErrors || artwork?.errors;
  if (!validation_errors && response?.error) {
    validation_errors = [response.error];
  }

  const pdp_type = pdpType;

  let multi_artwork = multiArtwork;
  if (multi_artwork === undefined) {
    multi_artwork = false;
  }

  const catalog_number = catalogNumber;
  const business_line = businessLine;
  const order_type = orderType;
  const success = explicitSuccess ?? artwork?.success ?? response?.success ?? null;

  const data = {
    filetype: uploadedFile.type,
    filesize: uploadedFile.size,
    filename: uploadedFile.name,
    artwork_filetype: artwork?.filetype,
    artwork_filesize: artwork?.filesize
  };

  const payload = {
    data,
    upload_speed_megabits_per_second,
    metadata,
    catalog_number,
    page,
    pdp_type,
    validation_errors,
    multi_artwork,
    project_id,
    artwork_id,
    image_url,
    business_line,
    success,
    worker_enhancement,
    ...(order_type && { order_type }),
    ...(uploadUuid && { upload_uuid: uploadUuid })
  };

  submitDtfEvent("image_upload_completed", payload);

  trackRudderstackImageUploadCompleted({
    location: page,
    file: uploadedFile,
    artwork,
    dpi: dpi || 300,
    business_line: businessLine,
    catalog_number: catalogNumber,
    pre_cut_transfers: preCutTransfers,
    artworks_count: artworksCount || 1
  });
};
